// Web Printing Utility for Kitchen, Beverage, and Receipt printing
//
// Strategy: render every receipt as a pre-formatted plain-text block
// (monospace, fixed column count). On mobile, the "Receipt Printer"
// Android app receives this text directly from the print job and uses
// its native thermal-printer font — which always fills the paper at
// the correct size, with no scaling / margins / image-shrink issues
// that the previous HTML+image approach suffered from.
//
// The browser printing pipeline:
//   tap PRINT → opens a new tab containing only a <pre> block →
//   user taps PRINT in tab → OS share sheet → Receipt Printer app →
//   thermal print at full paper width.

// ────────────────────────────────────────────────────────────────────
// Text helpers
// ────────────────────────────────────────────────────────────────────

// Thermal-printer column count. 32 cols is the canonical width that
// covers both 58mm and 80mm paper — the printer driver picks an
// appropriate font size for the configured paper.
const COLS = 32;

const padRight = (s, n) => {
  s = String(s ?? '');
  if (s.length >= n) return s.slice(0, n);
  return s + ' '.repeat(n - s.length);
};

const padLeft = (s, n) => {
  s = String(s ?? '');
  if (s.length >= n) return s.slice(0, n);
  return ' '.repeat(n - s.length) + s;
};

const center = (s, n = COLS) => {
  s = String(s ?? '');
  if (s.length >= n) return s.slice(0, n);
  const pad = n - s.length;
  const left = Math.floor(pad / 2);
  const right = pad - left;
  return ' '.repeat(left) + s + ' '.repeat(right);
};

const line = (ch = '-', n = COLS) => ch.repeat(n);

// Wrap a long string onto multiple lines at COL-width word boundaries.
const wrap = (s, n = COLS) => {
  s = String(s ?? '');
  if (s.length <= n) return [s];
  const words = s.split(/\s+/);
  const out = [];
  let cur = '';
  for (const w of words) {
    if (!cur) {
      cur = w.length > n ? w.slice(0, n) : w;
      if (w.length > n) {
        // Hard-split a single huge word.
        out.push(cur);
        cur = w.slice(n);
        while (cur.length > n) { out.push(cur.slice(0, n)); cur = cur.slice(n); }
      }
      continue;
    }
    if ((cur + ' ' + w).length <= n) cur += ' ' + w;
    else { out.push(cur); cur = w; }
  }
  if (cur) out.push(cur);
  return out;
};

// Two-column row: left text + right price, padded to COLS.
const row = (leftText, rightText, n = COLS) => {
  const right = String(rightText ?? '');
  const maxLeft = n - right.length - 1; // 1 space gap between cols
  const leftLines = wrap(leftText, maxLeft);
  // First line gets the right value, remaining left-only lines are blank on the right.
  const firstSpacing = ' '.repeat(n - leftLines[0].length - right.length);
  const lines = [leftLines[0] + firstSpacing + right];
  for (let i = 1; i < leftLines.length; i++) lines.push(padRight(leftLines[i], n));
  return lines.join('\n');
};

// Format Vietnamese dong with the trailing "đ".
const vnd = (amount) => {
  const n = Math.round((Number(amount) || 0) * 1000);
  return n.toLocaleString('en-US') + 'đ';
};

const fmtDateTime = (d) => {
  try {
    const dt = (d instanceof Date) ? d : new Date(d);
    if (isNaN(dt.getTime())) return '';
    const pad = (x) => String(x).padStart(2, '0');
    return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
  } catch (_) {
    return '';
  }
};

// Wrap pre-formatted text in a minimal monospace HTML document.
// Optional `qrUrl` appends a centered QR-code image below the text
// (used on customer receipts for review / payment QR).
const wrapAsPrintHtml = (title, textBlock, qrUrl = null, qrLabel = '') => `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  <style>
    @page { size: 80mm auto; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { background: #ffffff; }
    body {
      font-family: 'Courier New', 'Liberation Mono', monospace;
      color: #000;
      font-size: 12pt;
      line-height: 1.25;
      width: 100%;
      padding: 4mm 3mm;
    }
    pre.receipt {
      font-family: 'Courier New', 'Liberation Mono', monospace;
      font-size: 12pt;
      line-height: 1.25;
      white-space: pre;
      margin: 0;
      padding: 0;
      letter-spacing: 0;
      color: #000;
    }
    .qr-block {
      text-align: center;
      margin: 6mm 0 4mm;
      padding: 2mm 0;
      border-top: 1px dashed #000;
      border-bottom: 1px dashed #000;
    }
    .qr-block img {
      width: 35mm;
      height: 35mm;
      display: block;
      margin: 0 auto 2mm;
    }
    .qr-block .qr-caption {
      font-size: 10pt;
      color: #000;
    }
    @media print {
      body { padding: 0 !important; }
      pre.receipt { font-size: 11pt; }
      .qr-block { margin: 4mm 0 2mm; }
      .qr-block img { width: 30mm; height: 30mm; }
    }
  </style>
</head>
<body>
<pre class="receipt">${textBlock}</pre>
${qrUrl ? `<div class="qr-block"><img src="${qrUrl}" alt="QR" /><div class="qr-caption">${qrLabel || 'Quet de danh gia / Scan to review'}</div></div>` : ''}
</body>
</html>`;

// ────────────────────────────────────────────────────────────────────
// Text receipt builders
// ────────────────────────────────────────────────────────────────────

const buildKitchenText = (order, settings = {}) => {
  const items = order.items || [];
  const lines = [];
  lines.push(center('KITCHEN'));
  lines.push(center(`#${order.order_number}`));
  lines.push(center(`TABLE ${order.table || 'N/A'}`));
  lines.push(line('='));
  lines.push(`Time: ${fmtDateTime(order.created_at)}`);
  lines.push(line('-'));
  items.forEach((item) => {
    const qty = `${item.quantity}x`;
    const name = item.name?.en || item.name?.vi || item.name || '';
    const itemLines = wrap(`${qty} ${name}`, COLS);
    itemLines.forEach((l) => lines.push(l));
    if (item.special_note) {
      wrap(`  -> ${item.special_note}`, COLS).forEach((l) => lines.push(l));
    }
  });
  lines.push(line('-'));
  lines.push(center(settings.restaurant_name || 'BENZ EATERY'));
  lines.push(center(`Printed: ${fmtDateTime(new Date())}`));
  lines.push('');
  lines.push('');
  lines.push('');
  return lines.join('\n');
};

const buildBeverageText = (order, settings = {}) => {
  const items = order.items || [];
  const lines = [];
  lines.push(center('BAR / BEVERAGE'));
  lines.push(center(`#${order.order_number}`));
  lines.push(center(`TABLE ${order.table || 'N/A'}`));
  lines.push(line('='));
  lines.push(`Time: ${fmtDateTime(order.created_at)}`);
  lines.push(line('-'));
  items.forEach((item) => {
    const qty = `${item.quantity}x`;
    const name = item.name?.en || item.name?.vi || item.name || '';
    wrap(`${qty} ${name}`, COLS).forEach((l) => lines.push(l));
    if (item.special_note) {
      wrap(`  -> ${item.special_note}`, COLS).forEach((l) => lines.push(l));
    }
  });
  lines.push(line('-'));
  lines.push(center(settings.restaurant_name || 'BENZ EATERY'));
  lines.push(center(`Printed: ${fmtDateTime(new Date())}`));
  lines.push('');
  lines.push('');
  lines.push('');
  return lines.join('\n');
};

const buildReceiptText = (order, settings = {}) => {
  const items = order.items || [];
  const subtotal = order.subtotal || items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const discountPercent = order.discount_percent || 0;
  const discount = order.discount || 0;
  const cardFeePercent = order.card_fee_percent || 0;
  const cardFee = order.card_fee || 0;
  const total = order.total || (subtotal - discount + cardFee);
  const paymentMethod = order.payment_method || 'cash';

  const lines = [];
  lines.push(center(settings.restaurant_name || 'BENZ EATERY'));
  if (settings.address) lines.push(center(settings.address));
  if (settings.phone) lines.push(center(`Tel: ${settings.phone}`));
  lines.push(line('='));
  lines.push(center('HOA DON / RECEIPT'));
  lines.push(line('='));
  lines.push(`Ban/Table : ${order.table || 'N/A'}`);
  lines.push(`Ngay/Date : ${fmtDateTime(order.created_at)}`);
  if (order.order_number) lines.push(`So/Order  : #${order.order_number}`);
  lines.push(line('-'));
  items.forEach((item) => {
    const qty = `${item.quantity}x`;
    const name = item.name?.en || item.name?.vi || item.name || '';
    const price = vnd(item.price * item.quantity);
    lines.push(row(`${qty} ${name}`, price));
  });
  lines.push(line('-'));
  lines.push(row('Tam tinh/Subtotal:', vnd(subtotal)));
  if (discount > 0) {
    const label = discountPercent ? `Giam gia (${discountPercent}%):` : 'Giam gia/Discount:';
    lines.push(row(label, '-' + vnd(discount)));
  }
  if (cardFee > 0) {
    const label = cardFeePercent ? `Phi the (${cardFeePercent}%):` : 'Phi the/Bank fee:';
    lines.push(row(label, '+' + vnd(cardFee)));
  }
  lines.push(line('='));
  lines.push(row('TONG CONG/TOTAL:', vnd(total)));
  lines.push(line('='));
  lines.push(center(
    paymentMethod === 'cash' ? 'Thanh toan: TIEN MAT / CASH' : 'Thanh toan: THE / CARD'
  ));
  lines.push('');
  const footer = settings.footer_text || 'Cam on quy khach! / Thank you!';
  wrap(footer, COLS).forEach((l) => lines.push(center(l)));
  lines.push(center(fmtDateTime(new Date())));
  lines.push('');
  lines.push('');
  lines.push('');
  return lines.join('\n');
};

// ────────────────────────────────────────────────────────────────────
// Public HTML generators (kept same names so callers don't change)
// ────────────────────────────────────────────────────────────────────

export const generateKitchenTicketHTML = (order, settings = {}) =>
  wrapAsPrintHtml(`Kitchen #${order.order_number}`, buildKitchenText(order, settings));

export const generateBeverageTicketHTML = (order, settings = {}) =>
  wrapAsPrintHtml(`Bar #${order.order_number}`, buildBeverageText(order, settings));

export const generateReceiptHTML = (order, settings = {}) =>
  wrapAsPrintHtml(
    `Receipt #${order.order_number || order.table}`,
    buildReceiptText(order, settings),
    settings.qr_code_url || null,
    settings.qr_caption || 'Quet de danh gia / Scan to review',
  );

// ────────────────────────────────────────────────────────────────────
// Print via dialog. Opens a new tab with the text receipt, then
// triggers window.print() inside it. The host page is never involved
// so the receipt-printer app sees only the text receipt.
// ────────────────────────────────────────────────────────────────────

export const printWithDialog = (htmlContent) => {
  console.log('[Benz Print v11-text]');

  // Wrap the text receipt in a viewer doc with PRINT / SHARE buttons
  // so on mobile the user can either print directly OR share the
  // raw text/image to the Receipt Printer app.
  const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const styleMatch = htmlContent.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
  const bodyContent = bodyMatch ? bodyMatch[1] : htmlContent;
  const styleContent = styleMatch ? styleMatch[1] : '';

  const viewerHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Receipt — Benz Eatery</title>
  <style>
    ${styleContent}
    /* Viewer chrome (hidden during print) */
    .toolbar {
      position: fixed; left: 0; right: 0; bottom: 0;
      padding: 12px;
      background: #111;
      display: flex; gap: 6px; justify-content: center;
      box-shadow: 0 -2px 12px rgba(0,0,0,0.3);
      z-index: 10000;
    }
    .toolbar button {
      flex: 1; max-width: 160px;
      padding: 14px 8px;
      font-size: 14px; font-weight: 700; letter-spacing: 0.5px;
      border: 0; border-radius: 8px; cursor: pointer;
    }
    .toolbar .print { background: #ef4444; color: #fff; }
    .toolbar .share { background: #10b981; color: #fff; }
    .toolbar .close { background: #fff; color: #111; }
    .spacer { height: 96px; }
    @media print {
      .toolbar, .spacer { display: none !important; }
    }
  </style>
</head>
<body>
${bodyContent}
<div class="spacer"></div>
<div class="toolbar">
  <button class="print" onclick="window.print()">PRINT</button>
  <button class="share" id="shareBtn">SHARE</button>
  <button class="close" onclick="window.close()">CLOSE</button>
</div>
<script>
  (function () {
    document.getElementById('shareBtn').addEventListener('click', async function () {
      try {
        var pre = document.querySelector('pre.receipt');
        var text = pre ? pre.textContent : document.body.innerText;
        if (navigator.share) {
          await navigator.share({ title: 'Benz Eatery Receipt', text: text });
          return;
        }
        // Fallback: copy text to clipboard so user can paste into Receipt Printer.
        try {
          await navigator.clipboard.writeText(text);
          alert('Receipt copied to clipboard. Open Receipt Printer app and paste.');
        } catch (e) {
          alert('Share not supported on this device. Use PRINT instead.');
        }
      } catch (e) {
        alert('Share failed: ' + (e && e.message ? e.message : e));
      }
    });
  })();
</script>
</body>
</html>`;

  // Open new tab and write the viewer document.
  let printWin = null;
  try {
    printWin = window.open('', '_blank');
    if (printWin && printWin.document) {
      printWin.document.open();
      printWin.document.write(viewerHtml);
      printWin.document.close();
      return;
    }
  } catch (e) {
    console.warn('[Benz Print v11-text] popup blocked, falling back to host-page overlay:', e);
  }

  // Fallback (popup blocked): inline the receipt in this tab, hide rest of body.
  const overlay = document.createElement('div');
  overlay.id = '__benz_print_overlay__';
  overlay.style.cssText = 'position:fixed;left:0;top:0;right:0;bottom:0;z-index:2147483647;background:#fff;overflow:auto;padding:0;';
  overlay.innerHTML = viewerHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i)[1];

  // Insert the styles too.
  const styleTag = document.createElement('style');
  styleTag.textContent = styleContent + `
    @media print { body > *:not(#__benz_print_overlay__) { display: none !important; } }
  `;
  document.head.appendChild(styleTag);
  document.body.appendChild(overlay);

  const cleanup = () => {
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    if (styleTag.parentNode) styleTag.parentNode.removeChild(styleTag);
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
};

// ────────────────────────────────────────────────────────────────────
// Silent print kept for the auto-print path (kitchen/bar receipts on
// new orders). Hidden iframe is sufficient for plain-text printing.
// ────────────────────────────────────────────────────────────────────

export const silentPrint = (htmlContent) => new Promise((resolve, reject) => {
  try {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:absolute;top:-9999px;left:-9999px;width:80mm;height:0;border:0;';
    document.body.appendChild(iframe);
    const doc = (iframe.contentWindow || iframe.contentDocument).document;
    doc.open();
    doc.write(htmlContent);
    doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          setTimeout(() => { document.body.removeChild(iframe); resolve(true); }, 1000);
        } catch (e) {
          document.body.removeChild(iframe);
          reject(e);
        }
      }, 100);
    };
  } catch (error) {
    reject(error);
  }
});

export const autoPrintOrder = async (order, type = 'kitchen', settings = {}, silent = true) => {
  let htmlContent;
  if (type === 'kitchen') htmlContent = generateKitchenTicketHTML(order, settings);
  else if (type === 'beverage') htmlContent = generateBeverageTicketHTML(order, settings);
  else if (type === 'receipt') htmlContent = generateReceiptHTML(order, settings);
  else htmlContent = generateKitchenTicketHTML(order, settings);

  if (silent) {
    try {
      await silentPrint(htmlContent);
      return true;
    } catch (error) {
      printWithDialog(htmlContent);
      return true;
    }
  } else {
    printWithDialog(htmlContent);
    return true;
  }
};

export default {
  generateKitchenTicketHTML,
  generateBeverageTicketHTML,
  generateReceiptHTML,
  printWithDialog,
  silentPrint,
  autoPrintOrder,
};
